/* eslint-disable rxjs/no-ignored-subscription */
import { Inject, Injectable } from "@angular/core";
import { parseJwt } from "@obg/common.login/login/utils/jwt/jwt.util";
import { generateVersion4GUID } from "@obg/global.evo";
import { CustomerFraudToken, FraudPreventionConfigService, FraudPreventionProvider } from "@obg/ngx.common";
import { STARTUP_CONTEXT_TOKEN, StartupContext } from "@obg/ngx.core";
import { DeviceExperience, DocumentRef, isLocalStorageSupported, loadScript } from "@obg/ngx.global";
import { CookieService, PlatformService } from "@obg/ngx.global.evo";
import { BehaviorSubject, Observable } from "rxjs";
import { map } from "rxjs/operators";

import { GroupIbGlobalObject } from "./fraud-prevention.model";
import { FraudPreventionService } from "./fraud-prevention.service";

declare global {
	// eslint-disable-next-line no-var
	var gib: GroupIbGlobalObject | undefined;
	// eslint-disable-next-line no-var
	var onGibLoad: (() => void) | undefined;
}

const GROUPIB_SESSION_ID_STORAGE_KEY = "groupIbSessionId";
const GROUPIB_NATIVE_COOKIE_KEY = "native_groupIB_session";

@Injectable({
	providedIn: "root",
})
export class GroupIBFraudPreventionService extends FraudPreventionService {
	private readonly scriptSrc!: string;
	private readonly customerId!: string;
	private readonly backUrl!: string;
	private readonly fraudToken$: BehaviorSubject<string | undefined> = new BehaviorSubject(undefined);

	constructor(
		private platformService: PlatformService,
		private fraudPreventionConfigService: FraudPreventionConfigService,
		private cookieService: CookieService,
		@Inject(STARTUP_CONTEXT_TOKEN) private startupContext: StartupContext,
		private documentRef: DocumentRef
	) {
		super();
		if (this.platformService.isServer || globalThis.isDOMSSR) {
			return;
		}

		if (this.startupContext.appContext.deviceExperience === DeviceExperience.Native) {
			const nativeCookie = this.cookieService.get(GROUPIB_NATIVE_COOKIE_KEY);

			if (nativeCookie) {
				this.fraudToken$.next(nativeCookie);

				return;
			}
		}

		this.scriptSrc = this.fraudPreventionConfigService.getGroupIBScriptSrc();
		this.customerId = this.fraudPreventionConfigService.getGroupIBCustomerId();
		this.backUrl = this.fraudPreventionConfigService.getGroupIBBackUrl();
		if (this.scriptSrc && this.customerId && this.backUrl) {
			this.loadGIBScript();
		}
	}

	loadGIBScript() {
		loadScript({
			src: this.scriptSrc,
			id: `fraud-prevention-${FraudPreventionProvider.GroupIB}`,
		}).then(() => {
			if (typeof globalThis.gib === "undefined") {
				globalThis.onGibLoad = () => {
					this.initScript();
				};
			} else {
				this.initScript();
			}
		});
	}

	getSessionId(): string | undefined {
		return this.fraudToken$?.value;
	}

	getSessionId$(): Observable<string | undefined> {
		return this.fraudToken$;
	}

	initScript() {
		const cookieDomain: string = this.documentRef.native.location!.hostname.replace(/^www(.*?)\./, "");

		globalThis.gib!.init({
			cid: this.customerId,
			backUrl: this.backUrl,
			gafUrl: "//eu.id.group-ib.com/id.html",
			cookiesDomain: [cookieDomain],
		});

		this.loadOrGenerateSessionId();
	}

	setAuthorized(sessionToken?: string | undefined) {
		if (globalThis.isDOMSSR || !globalThis.gib) return;
		const sessionData: Record<string, any> | undefined = parseJwt(sessionToken ?? "");
		const userId: string | undefined = sessionData?.userId;

		this.loadOrGenerateSessionId();

		if (userId) globalThis.gib.setIdentity(userId);
		globalThis.gib.setAuthStatus(globalThis.gib.IS_AUTHORIZED);
		globalThis.gib.setSessionID(this.fraudToken$.value!);
	}

	setUnauthorized() {
		if (!globalThis.gib) return;
		this.generateSessionId();
		globalThis.gib.setAuthStatus(globalThis.gib.IS_GUEST);
		globalThis.gib.setSessionID(this.fraudToken$.value!);
	}

	setNewToken() {
		if (!globalThis.gib) return;
		this.generateSessionId();
		globalThis.gib.setSessionID(this.fraudToken$.value!);
	}

	private loadOrGenerateSessionId(): void {
		const storedSessionId: string | undefined = this.getStoredSessionId();

		if (!storedSessionId) {
			return this.generateSessionId();
		}

		if (storedSessionId !== this.fraudToken$.value) {
			this.fraudToken$.next(storedSessionId);
		}
	}

	private getStoredSessionId(): string | undefined {
		if (!isLocalStorageSupported()) {
			return undefined;
		}
		return localStorage.getItem(GROUPIB_SESSION_ID_STORAGE_KEY) || undefined;
	}
	private generateSessionId(): void {
		const generatedToken: string = generateVersion4GUID();
		this.fraudToken$.next(generatedToken);
		if (isLocalStorageSupported()) {
			localStorage.setItem(GROUPIB_SESSION_ID_STORAGE_KEY, generatedToken);
		}
	}

	override getCustomerFraudToken$(): Observable<CustomerFraudToken | undefined> {
		return this.fraudToken$.pipe(map(() => ({ sessionToken: this.getSessionId() ?? "", url: "" })));
	}

	override init(): void {
		// GroupIB should be loaded immediately after page load and should not be removed
	}

	override loadScript(): void {
		// GroupIB should be loaded immediately after page load and should not be removed
	}

	override unloadScript(): void {
		// GroupIB should be loaded immediately after page load and should not be removed
	}
}
